'use strict';

function getParent() {
    if (cameraFollower) {
        return cameraFollower;
    }

    if (iq3Camera && iq3Camera.camera) {
        return iq3Camera.camera;
    }

    return VR.scene;
}

function registerMenuAdapter(contentContainer, buttons) {
    if (!contentContainer || !buttons.length) return null;

    let cachedClickableComponents = null;

    function rebuildClickableComponentsCache() {
        const components = [];
        if (buttons && buttons.length > 0) {
            buttons.filter((b) => b.visible && isDescendant(b, VR.scene)).forEach((b) => components.push(b));
        }
        cachedClickableComponents = components;
    }

    const adapter = {
        type: 'ARAuthoringAlignmentPrompt',
        contentContainer,
        buttons,

        isPartOfMenu(object) {
            if (!object || !contentContainer || object === contentContainer) {
                return false;
            }
            
            let node = object.parent;
            while (node) {
                if (node === contentContainer) return true;
                node = node.parent;
            }
            return false;
        },

        getObjectFromIntersection(object) {
            while (object) {
                if (buttons.includes(object)) return object;
                object = object.parent;
            }
            return null;
        },

        getFirstMenuIntersection(intersections) {
            if (!intersections || intersections.length === 0) return null;
            const intersection = intersections[0];
            if (intersection && intersection.object && adapter.isPartOfMenu(intersection.object)) return intersection;
            return null;
        },

        setButtonsHoverState(hoveredButtons = []) {
            buttons.forEach((button) => {
                button.setState(hoveredButtons.includes(button) ? 'hovered' : 'idle');
            });
        },

        handleHoverState(intersections, showPointer = null, controller = null) {
            const hoveredButtons = [];
            const intersection = adapter.getFirstMenuIntersection(intersections);
            if (intersection) {
                const object = adapter.getObjectFromIntersection(intersection.object);
                if (object && object.visible) {
                    hoveredButtons.push(object);
                    if (showPointer && controller) showPointer(controller, intersection);
                }
            }
            adapter.setButtonsHoverState(hoveredButtons);
            return hoveredButtons;
        },

        handleClickState(intersections) {
            const intersection = adapter.getFirstMenuIntersection(intersections);
            if (!intersection) return false;
            const object = adapter.getObjectFromIntersection(intersection.object);
            if (object && object.visible && buttons.includes(object)) {
                object.setState('selected');
            }
            return true;
        },

        detectMenuClick(intersections) {
            return !!adapter.getFirstMenuIntersection(intersections);
        },

        getAllClickableComponents() {
            if (!cachedClickableComponents) {
                rebuildClickableComponentsCache();
            }
            return cachedClickableComponents;
        },

        invalidateClickableComponentsCache() {
            cachedClickableComponents = null;
        },

        handleMouseMove(event) {
            const allComponents = adapter.getAllClickableComponents();
            const intersectedObjects = getIntersectionFromMouseEvent(event, allComponents);
            adapter.handleHoverState(intersectedObjects);
        },

        handleMouseClick(event) {
            const allComponents = adapter.getAllClickableComponents();
            const intersectedObjects = getIntersectionFromMouseEvent(event, allComponents);
            return adapter.handleClickState(intersectedObjects);
        },

        onMouseDown(event) {
            const allComponents = adapter.getAllClickableComponents();
            setMouseClickRaycaster(event);
            const hits = raycast.intersectObjects(allComponents, true);
            return adapter.detectMenuClick(hits);
        },

        onControllerMove(controller1, controller2) {
            const controllers = [controller1, controller2];
            const allComponents = adapter.getAllClickableComponents();
            controllers.forEach((controller) => {
                if (controller) {
                    setControllerRaycaster(controller);
                    const intersectedObjects = raycast.intersectObjects(allComponents, true);
                    adapter.handleHoverState(intersectedObjects, null, controller);
                }
            });
        },

        onTriggerDown(controller) {
            const allComponents = adapter.getAllClickableComponents();
            setControllerRaycaster(controller);
            const hits = raycast.intersectObjects(allComponents, true);
            return adapter.handleClickState(hits);
        }
    };

    threeMeshMenuControllers.push(adapter);
    rebuildClickableComponentsCache();
    return adapter;
}

function unregisterMenuAdapter(adapter) {
    if (!adapter) return;
    if (typeof adapter.invalidateClickableComponentsCache === 'function') {
        adapter.invalidateClickableComponentsCache();
    }
    if (threeMeshMenuControllers) {
        const idx = threeMeshMenuControllers.indexOf(adapter);
        if (idx !== -1) threeMeshMenuControllers.splice(idx, 1);
    }
}

function disposeThreeMeshUIRoot(root) {
    if (!root) return;

    if (root.parent) {
        root.parent.remove(root);
    }

    const disposeGeometryAndMaterial = (obj) => {
        if (!obj) return;
        if (obj.geometry) {
            obj.geometry.dispose();
            obj.geometry = null;
        }
        if (obj.material) {
            if (Array.isArray(obj.material)) {
                obj.material.forEach((m) => { if (m) m.dispose(); });
            } else {
                obj.material.dispose();
            }
            obj.material = null;
        }
    };

    root.traverse((child) => {
        disposeGeometryAndMaterial(child);
        if (child.frame) {
            disposeGeometryAndMaterial(child.frame);
            child.frame = null;
        }
        if (child.textContent && child.textContent.traverse) {
            child.textContent.traverse((desc) => disposeGeometryAndMaterial(desc));
        }
    });

    if (ThreeMeshUI.UpdateManager) {
        root.traverse((child) => {
            if (child.isUI) {
                ThreeMeshUI.UpdateManager.disposeOf(child);
            }
        });
    }

    if (root.clear) {
        root.clear();
    }

    while (root.children && root.children.length > 0) {
        root.remove(root.children[0]);
    }
}

const ALIGNMENT_METHOD_PROMPT_UI_CONFIG = {
    colors: {
        textContainerBg: '#2c3e50',
        text: '#FFFFFF',
        actionButton: '#3280b1',
        actionButtonHover: '#4a9fd4',
        cancelButton: '#c0392b',
        cancelButtonHover: '#e74c3c',
        selected: '#5EFF14'
    },
    layout: {
        contentWidth: 1.15,
        textBlockHeight: 0.18,
        buttonHeight: 0.08,
        buttonWidth: 0.36,
        gapTextToButtons: 0.025,
        textMargin: 0.045,
        borderRadius: 0.02
    },
    transparentBlock: {
        backgroundOpacity: 0,
        borderWidth: 0,
        offset: 0.000001
    },
    textPanel: {
        backgroundOpacity: 0.95,
        borderWidth: 0.002,
        offset: 0.002,
        padding: 0,
        margin: 0
    },
    button: {
        borderWidth: 0.002,
        padding: 0.008,
        offset: 0.004,
        backgroundOpacity: 1
    },
    typography: {
        bodyFontSize: 0.038,
        buttonLabelFontSize: 0.036,
        fontFamily: 'Roboto',
        fontTexture: 'Roboto'
    },
    copy: {
        prompt:
            'To get started, first choose how you would like to align the virtual and physical worlds.',
        buttonLabels: ['Virtual Model', 'Physical Object', 'Cancel']
    },
    methods: ['virtual', 'physical', 'cancel'],
    world: {
        containerPositionZ: -1.2
    },
    renderOrder: {
        textOnPanel: 2,
        buttonBlock: 2,
        buttonLabel: 3
    }
};

export function createAlignmentMethodPromptUI(options) {
    const onChoice = options.onChoice || (() => {});
    const cfg = ALIGNMENT_METHOD_PROMPT_UI_CONFIG;
    const { colors, layout, transparentBlock, textPanel, button, typography, copy, methods, world, renderOrder } = cfg;

    const parent = getParent();
    if (!parent) {
        VRMessageController.addMessage({ text: 'AR Authoring: Scene not ready.', duration: 3000 });
        return { remove: () => {} };
    }

    const contentWidth = layout.contentWidth;
    const textBlockHeight = layout.textBlockHeight;
    const buttonHeight = layout.buttonHeight;

    const textBlock = new ThreeMeshUI.Block({
        width: contentWidth,
        height: textBlockHeight,
        justifyContent: 'center',
        alignItems: 'start',
        textAlign: ThreeMeshUI.TextAlign.JUSTIFY_LEFT,
        backgroundColor: new THREE.Color(colors.textContainerBg),
        backgroundOpacity: textPanel.backgroundOpacity,
        borderRadius: layout.borderRadius,
        borderWidth: textPanel.borderWidth,
        borderColor: new THREE.Color(colors.text),
        padding: textPanel.padding,
        margin: textPanel.margin,
        offset: textPanel.offset,
        transparent: false,
        depthWrite: true,
        depthTest: true,
        renderOrder: RenderOrderEnum.AddMenuDefaultRO
    });
    const textInner = new ThreeMeshUI.Block({
        width: contentWidth - layout.textMargin * 2,
        height: textBlockHeight - layout.textMargin * 2,
        justifyContent: 'center',
        alignItems: 'start',
        textAlign: ThreeMeshUI.TextAlign.JUSTIFY_LEFT,
        backgroundOpacity: transparentBlock.backgroundOpacity,
        borderWidth: transparentBlock.borderWidth,
        margin: layout.textMargin,
        offset: transparentBlock.offset
    });
    textInner.add(new ThreeMeshUI.Text({
        content: copy.prompt,
        fontSize: typography.bodyFontSize,
        fontColor: new THREE.Color(colors.text),
        fontFamily: typography.fontFamily,
        fontTexture: typography.fontTexture,
        whiteSpace: 'normal',
        renderOrder: RenderOrderEnum.AddMenuDefaultRO + renderOrder.textOnPanel,
        textAlign: ThreeMeshUI.TextAlign.JUSTIFY_LEFT
    }));
    textBlock.add(textInner);

    const spacerBetweenTextAndButtons = new ThreeMeshUI.Block({
        width: contentWidth,
        height: layout.gapTextToButtons,
        backgroundOpacity: transparentBlock.backgroundOpacity,
        borderWidth: transparentBlock.borderWidth,
        margin: 0,
        offset: transparentBlock.offset
    });

    const buttonRow = new ThreeMeshUI.Block({
        contentDirection: 'row',
        width: contentWidth,
        height: buttonHeight,
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundOpacity: transparentBlock.backgroundOpacity,
        borderWidth: transparentBlock.borderWidth,
        margin: 0,
        offset: transparentBlock.offset
    });

    const labels = copy.buttonLabels;
    const isCancel = (method) => method === 'cancel';
    const buttons = [];

    methods.forEach((method, index) => {
        const idleBg = isCancel(method) ? colors.cancelButton : colors.actionButton;
        const hoverBg = isCancel(method) ? colors.cancelButtonHover : colors.actionButtonHover;
        const btn = new ThreeMeshUI.Block({
            width: layout.buttonWidth,
            height: buttonHeight,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: new THREE.Color(idleBg),
            backgroundOpacity: button.backgroundOpacity,
            borderColor: new THREE.Color(idleBg),
            borderWidth: button.borderWidth,
            borderRadius: layout.borderRadius,
            padding: button.padding,
            offset: button.offset,
            transparent: false,
            depthWrite: true,
            depthTest: true,
            renderOrder: RenderOrderEnum.AddMenuDefaultRO + renderOrder.buttonBlock
        });
        btn.name = 'arAuthoringAlignButton' + index;
        btn.add(new ThreeMeshUI.Text({
            content: labels[index],
            fontSize: typography.buttonLabelFontSize,
            fontColor: new THREE.Color(colors.text),
            fontFamily: typography.fontFamily,
            fontTexture: typography.fontTexture,
            renderOrder: RenderOrderEnum.AddMenuDefaultRO + renderOrder.buttonLabel,
            textAlign: ThreeMeshUI.TextAlign.CENTER
        }));
        btn.setupState({
            state: 'idle',
            attributes: {
                backgroundColor: new THREE.Color(idleBg),
                borderColor: new THREE.Color(idleBg)
            }
        });
        btn.setupState({
            state: 'hovered',
            attributes: {
                backgroundColor: new THREE.Color(hoverBg),
                borderColor: new THREE.Color(hoverBg)
            }
        });
        btn.setupState({
            state: 'selected',
            attributes: {
                backgroundColor: new THREE.Color(colors.selected),
                borderColor: new THREE.Color(colors.selected)
            },
            onSet: () => { onChoice(method); }
        });
        buttons.push(btn);
        buttonRow.add(btn);
    });

    const container = new ThreeMeshUI.Block({
        contentDirection: 'column',
        width: contentWidth,
        height: textBlockHeight + layout.gapTextToButtons + buttonHeight,
        justifyContent: 'start',
        alignItems: 'center',
        backgroundOpacity: transparentBlock.backgroundOpacity,
        borderWidth: transparentBlock.borderWidth,
        offset: transparentBlock.offset,
        renderOrder: RenderOrderEnum.AddMenuDefaultRO
    });
    container.add(textBlock);
    container.add(spacerBetweenTextAndButtons);
    container.add(buttonRow);
    parent.add(container);
    container.position.set(0, 0, world.containerPositionZ);
    container.rotation.set(0, 0, 0);

    ThreeMeshUI.update();

    const adapter = registerMenuAdapter(container, buttons);
    const root = container;

    function remove() {
        unregisterMenuAdapter(adapter);
        if (root) {
            disposeThreeMeshUIRoot(root);
        }
        ThreeMeshUI.update();
    }

    return { remove };
}

export default createAlignmentMethodPromptUI;
